import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { CheckCircle2, Loader2, XCircle, ArrowRight, Sparkles, CreditCard, Shield } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying');
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [error, setError] = useState(null);
  const depositId = searchParams.get('deposit_id');
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (depositId || sessionId) {
      pollPaymentStatus();
    } else {
      setStatus('success');
    }
  }, [depositId, sessionId]);

  const pollPaymentStatus = async (attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('success');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setStatus('success');
        return;
      }

      const response = await axios.get(`${API_URL}/deposits/stripe/check/${depositId || 'latest'}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.status === 'pending' || response.data.status === 'approved') {
        setPaymentInfo(response.data);
        setStatus('success');
        return;
      }

      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    } catch (error) {
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
      } else {
        setStatus('success');
      }
    }
  };

  if (status === 'verifying') {
    return (
      <div className="min-h-screen bg-[#0A0E14] flex items-center justify-center p-4">
        <div className="max-w-lg w-full">
          <div className="glass-card p-8 text-center border border-white/10 rounded-3xl bg-gradient-to-b from-white/5 to-transparent">
            <div className="w-24 h-24 rounded-full bg-blue-500/10 flex items-center justify-center mx-auto mb-6 animate-pulse">
              <Loader2 className="w-12 h-12 text-blue-400 animate-spin" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2 font-['Outfit']">
              {t('paymentPages.verifyingPayment')}
            </h2>
            <p className="text-slate-400">
              {t('paymentPages.pleaseWait')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'error' || status === 'expired' || status === 'timeout') {
    return (
      <div className="min-h-screen bg-[#0A0E14] flex items-center justify-center p-4">
        <div className="max-w-lg w-full">
          <div className="glass-card p-8 text-center border border-red-500/20 rounded-3xl bg-gradient-to-b from-red-500/5 to-transparent">
            <div className="w-24 h-24 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-12 h-12 text-red-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2 font-['Outfit']">
              {status === 'timeout' ? t('paymentPages.timeExpired') : t('paymentPages.paymentError')}
            </h2>
            <p className="text-slate-400 mb-6">
              {error || t('paymentPages.errorProcessing')}
            </p>
            <Button
              onClick={() => navigate('/deposit')}
              className="bg-blue-500 hover:bg-blue-600 text-white rounded-xl h-12 px-6"
              data-testid="try-again-btn"
            >
              {t('paymentPages.tryAgain')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0E14] flex items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
      </div>

      <div className="max-w-lg w-full relative z-10">
        <div className="text-center mb-8">
          <div className="relative inline-block">
            <div className="w-28 h-28 rounded-full bg-gradient-to-br from-emerald-500/20 to-emerald-500/5 flex items-center justify-center mx-auto mb-6 animate-bounce-slow border-2 border-emerald-500/30">
              <CheckCircle2 className="w-14 h-14 text-emerald-400" />
            </div>
            <Sparkles className="w-6 h-6 text-yellow-400 absolute -top-1 -right-1 animate-pulse" />
            <Sparkles className="w-4 h-4 text-emerald-400 absolute top-2 -left-2 animate-pulse" style={{ animationDelay: '0.5s' }} />
          </div>
        </div>

        <div className="glass-card p-8 text-center border border-emerald-500/20 rounded-3xl bg-gradient-to-b from-emerald-500/5 to-transparent backdrop-blur-xl">
          <h1 className="text-3xl font-bold text-white mb-2 font-['Outfit']">
            {t('paymentPages.paymentConfirmed')}
          </h1>
          <p className="text-slate-400 mb-6">
            {t('paymentPages.paymentProcessedSuccess')}
          </p>
          
          {paymentInfo?.amount && (
            <div className="bg-gradient-to-br from-emerald-500/20 to-emerald-500/5 border border-emerald-500/30 rounded-2xl p-6 mb-6">
              <div className="flex items-center justify-center gap-2 mb-2">
                <CreditCard className="w-5 h-5 text-emerald-400" />
                <span className="text-emerald-400 text-sm font-medium">{t('paymentPages.paidAmount')}</span>
              </div>
              <p className="text-emerald-400 text-4xl font-bold">
                €{paymentInfo.amount.toLocaleString('pt-PT', { minimumFractionDigits: 2 })}
              </p>
            </div>
          )}
          
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-2xl p-4 mb-6">
            <div className="flex items-center gap-2 justify-center mb-2">
              <Shield className="w-5 h-5 text-amber-400" />
              <span className="text-amber-400 font-medium">{t('paymentPages.nextStep')}</span>
            </div>
            <p className="text-slate-300 text-sm">
              {t('paymentPages.pendingApproval')}
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              onClick={() => navigate('/dashboard')}
              className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white rounded-xl h-12 px-6 font-semibold shadow-lg shadow-emerald-500/25 transition-all hover:scale-105"
              data-testid="go-to-dashboard-btn"
            >
              {t('paymentPages.goToDashboard')}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button
              onClick={() => navigate('/deposits')}
              variant="outline"
              className="bg-white/5 hover:bg-white/10 border-white/10 text-white rounded-xl h-12 px-6"
              data-testid="view-deposits-btn"
            >
              {t('paymentPages.viewMyPayments')}
            </Button>
          </div>
        </div>

        <div className="flex items-center justify-center gap-4 mt-6 text-slate-500 text-xs">
          <div className="flex items-center gap-1">
            <Shield className="w-4 h-4" />
            <span>{t('paymentPages.securePayment')}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
